import React, { useState, useEffect } from 'react';
import { Users, ArrowLeft, User, Phone, Save, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFlashMessage } from '../components/FlashMessage';
import { formatPhoneNumber, cleanPhoneNumber } from '../utils/phoneFormatter';
import { FormSelect } from '../components/FormSelect';

const OwnerEditUserForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    phone: '',
    full_name: '',
    location_id: '',
    role: 'staff'
  });

  const [userLocations, setUserLocations] = useState([]);

  useEffect(() => {
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadUserData = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await usersAPI.getUsers();
      if (response.data.success) {
        const userData = response.data.data.find(u => u.id === parseInt(userId));

        if (userData) {
          // Get user's first location (owner can change it)
          const userLocation = userData.locations && userData.locations.length > 0
            ? userData.locations[0]
            : null;

          // Find location ID from owner's locations
          const ownerLocation = user.locations.find(loc => loc.name === userLocation?.name);

          setFormData({
            username: userData.username || '',
            password: '',
            phone: formatPhoneNumber(userData.phone || ''),
            full_name: userData.full_name || '',
            location_id: ownerLocation?.id || (user.locations[0]?.id || ''),
            role: userLocation?.role || 'staff'
          });

          setUserLocations(userData.locations || []);
        } else {
          setError('Kullanıcı bulunamadı');
        }
      }
    } catch (err) {
      console.error('Failed to load user:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    if (formData.password && formData.password.length < 6) {
      showDanger('Şifre girilecekse en az 6 karakter olmalıdır');
      setIsSaving(false);
      return;
    }

    if (!formData.phone.trim()) {
      showDanger('Cep telefonu numarası zorunludur');
      setIsSaving(false);
      return;
    }

    try {
      const updateData = {
        phone: cleanPhoneNumber(formData.phone) || undefined,
        full_name: formData.full_name,
      };

      if (formData.password) {
        updateData.password = formData.password;
      }

      // Update user info
      await usersAPI.updateUser(userId, updateData);

      // Update location if changed
      if (formData.location_id) {
        await usersAPI.updateUserLocation(userId, {
          location_id: parseInt(formData.location_id),
          role: formData.role
        });
      }

      showSuccess('Personel başarıyla güncellendi!');
      setTimeout(() => {
        navigate('/owner/users');
      }, 2000);
    } catch (err) {
      console.error('Failed to update user:', err);
      showDanger(`Kullanıcı güncellenirken hata oluştu: ${handleApiError(err)}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!user?.isOwner) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
          <p className="text-red-700">Bu sayfaya erişim yetkiniz yok.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/owner/users"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="w-7 h-7 mr-3 text-blue-500" />
              Personel Düzenle
            </h1>
            <p className="text-gray-600">Personel bilgilerini güncelleyin</p>
          </div>
        </div>
      </div>

      {/* Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* User Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Personel Bilgileri
            </h3>
          </div>
          <div className="card-body space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  Kullanıcı Adı
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  className="form-input bg-gray-100"
                  disabled
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">
                  Kullanıcı adı değiştirilemez
                </p>
              </div>
              <div>
                <label className="form-label">
                  Tam Adı *
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Örn: Ahmet Yılmaz"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Cep Telefonu *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0555 123 45 67"
                  maxLength="14"
                  required
                />
              </div>

              <div>
                <label className="form-label">
                  Lokasyon *
                </label>
                <FormSelect
                  name="location_id"
                  value={formData.location_id}
                  onChange={handleInputChange}
                  options={user?.locations?.map(location => ({ value: location.id.toString(), label: location.name })) || []}
                  placeholder="Lokasyon Seçin"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Personelin çalışacağı lokasyonu seçin
                </p>
              </div>
            </div>

            <div>
              <label className="form-label">
                Şifre (Boş bırakılırsa değiştirilmez)
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="form-input pr-20"
                  placeholder="Yeni şifre (isteğe bağlı)"
                  minLength={6}
                />
                <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                  >
                    Oluştur
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Güvenli şifre için "Oluştur" butonunu kullanabilirsiniz
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <Link
            to="/owner/users"
            className="btn-secondary"
          >
            İptal
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary flex items-center"
          >
            {isSaving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Güncelleniyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Değişiklikleri Kaydet
              </>
            )}
          </button>
        </div>
      </form>

      {/* Flash Message Container */}
      <FlashContainer />
    </div>
  );
};

export default OwnerEditUserForm;
